import { nextTick, onBeforeUnmount, watch, type Ref } from 'vue'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** 为自定义弹窗统一处理首焦点、Tab 约束、Escape 关闭与焦点恢复。 */
export function useDialogFocus(
  open: Ref<boolean>,
  dialog: Ref<HTMLElement | null>,
  beforeClose?: () => boolean,
): void {
  let previousFocus: HTMLElement | null = null

  function focusableElements(): HTMLElement[] {
    if (!dialog.value) return []
    return Array.from(dialog.value.querySelectorAll<HTMLElement>(FOCUSABLE))
      .filter((element) => {
        if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true' || element.closest('[inert]')) return false
        const style = window.getComputedStyle(element)
        if (style.display === 'none' || style.visibility === 'hidden') return false
        // jsdom 没有布局引擎，getClientRects() 始终为空；真实浏览器中
        // 则用它排除被父级 display:none 的伪可聚焦节点。
        return /jsdom/i.test(window.navigator.userAgent) || element.getClientRects().length > 0
      })
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault()
      if (beforeClose && !beforeClose()) return
      open.value = false
      return
    }

    if (event.key !== 'Tab') return
    const focusable = focusableElements()
    if (focusable.length === 0) {
      event.preventDefault()
      dialog.value?.focus()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  watch(open, async (isOpen) => {
    if (isOpen) {
      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
      await nextTick()
      const first = focusableElements()[0]
      ;(first ?? dialog.value)?.focus()
      document.addEventListener('keydown', handleKeydown)
      return
    }

    document.removeEventListener('keydown', handleKeydown)
    const focusTarget = previousFocus
    // 抽屉关闭时触发器在当前 patch 内仍位于 inert 背景，
    // 等待属性移除后再聚焦；普通弹窗则可立即恢复。
    if (focusTarget?.closest('[inert]')) await nextTick()
    focusTarget?.focus()
    previousFocus = null
  })

  onBeforeUnmount(() => document.removeEventListener('keydown', handleKeydown))
}
